/**
 * Phase 5b — Standalone Tauri battle bridge.
 *
 * When the bundle runs inside `warlords-standalone` (the Tauri shell), the
 * orchestrator-spawned per-match worker doesn't exist. Instead, the host
 * standalone process runs `wargame_worker::spawn_embedded_match` in-process
 * and pipes per-player `TickFrame`s through the warlords.cc relay; every
 * other standalone connects to the same relay as a client. Both modes are
 * driven by Tauri commands (`start_local_battle` / `start_remote_battle` /
 * `submit_order` / `stop_battle`) and Tauri events
 * (`battle-frame` / `battle-end` / `battle-error`).
 *
 * `StandaloneBattleClient` mirrors the public surface of `WorkerWsClient`
 * (`isOpen`, `sendOrder`, `close`, the same callback shape) so the rest of
 * the network module can treat the two transports interchangeably.
 *
 * The actual call sites that decide WHEN to instantiate this client (which
 * standalone is host? what's the relay URL? what's the start packet?) live
 * with Phase 5c (SpacetimeDB host-election table). This module provides
 * only the bridge surface.
 */

import type {
  WorkerHelloOpts,
  WorkerMatchEnd,
  WorkerTickFrame,
  WorkerWsCallbacks,
} from "./worker-ws";

type TauriInvoke = (cmd: string, args?: any) => Promise<any>;
type UnlistenFn = () => void;
type EventListenFn = (
  event: string,
  handler: (event: { payload: any }) => void,
) => Promise<UnlistenFn>;

interface TauriBridge {
  invoke: TauriInvoke;
  listen: EventListenFn;
}

/** Returns true when the bundle is running inside the Tauri standalone
 *  shell. We detect on `__TAURI_INTERNALS__` (Tauri 2.x) rather than the
 *  older `__TAURI__` global. */
export function isStandaloneShell(): boolean {
  try {
    return typeof (window as any).__TAURI_INTERNALS__?.invoke === "function";
  } catch {
    return false;
  }
}

/** Returns true when the standalone bundle should drive battles through the
 *  Tauri bridge (vs. the legacy worker-WS path). Mirrors the env flag baked
 *  in by `warlords-standalone/app/build.mjs`. */
export function isStandaloneBattleEnabled(): boolean {
  if (!isStandaloneShell()) return false;
  if ((window as any).WARGAME_STANDALONE === false) return false;
  return true;
}

function getTauri(): TauriBridge | null {
  const internals = (window as any).__TAURI_INTERNALS__;
  if (!internals || typeof internals.invoke !== "function") return null;
  // Tauri 2 exposes the events API on `window.__TAURI__.event` only when the
  // JS API package is bundled. The standalone bundles `@tauri-apps/api`'s
  // event helpers via a lightweight shim below if not present.
  const eventApi = (window as any).__TAURI__?.event;
  const listen: EventListenFn = eventApi?.listen ?? fallbackListen;
  return { invoke: internals.invoke.bind(internals), listen };
}

/** Minimal event-listener shim that talks to the Tauri 2 IPC directly when
 *  the user-facing `@tauri-apps/api` package isn't bundled. Uses the same
 *  `__TAURI_INTERNALS__.transformCallback` + `addEventListener` mechanism
 *  the official package wraps. */
async function fallbackListen(
  event: string,
  handler: (ev: { payload: any }) => void,
): Promise<UnlistenFn> {
  const internals = (window as any).__TAURI_INTERNALS__;
  if (!internals || typeof internals.transformCallback !== "function") {
    throw new Error(
      "Tauri internals missing transformCallback; cannot listen for events",
    );
  }
  const cbId = internals.transformCallback(
    (ev: any) => handler({ payload: ev?.payload }),
    false,
  );
  // Tauri's event registration command returns a numeric id we use to
  // unlisten via `plugin:event|unlisten`.
  const unlistenId: number = await internals.invoke("plugin:event|listen", {
    event,
    target: { kind: "Any" },
    handler: cbId,
  });
  return async () => {
    try {
      await internals.invoke("plugin:event|unlisten", {
        event,
        eventId: unlistenId,
      });
    } catch {
      /* swallow on shutdown */
    }
  };
}

export interface StandaloneStartPlayer {
  player_id: string;
  army_index: number;
}

export type StandaloneMode =
  | {
      kind: "host";
      relayUrl: string;
      jwt: string;
      startPacket: any;
      players: StandaloneStartPlayer[];
      hostPlayerId: string;
    }
  | { kind: "client"; relayUrl: string; jwt: string };

/** Bridge that mirrors `WorkerWsClient`'s public shape. The constructor
 *  takes a `WorkerHelloOpts` only so callers can reuse the same plumbing
 *  (matchId / playerId); the JWT/relay url come from `mode`. */
export class StandaloneBattleClient {
  private cb: WorkerWsCallbacks;
  private hello: WorkerHelloOpts;
  private mode: StandaloneMode;
  private bridge: TauriBridge | null;
  private unlistenFns: UnlistenFn[] = [];
  private _open = false;
  private _closed = false;
  private _lastTick = 0;
  /** Orders submitted before the Tauri command resolves are buffered here
   *  and replayed once `_open` flips. */
  private pending: { action: string; payload: any }[] = [];

  constructor(
    hello: WorkerHelloOpts,
    mode: StandaloneMode,
    callbacks: WorkerWsCallbacks = {},
  ) {
    this.hello = hello;
    this.mode = mode;
    this.cb = callbacks;
    this.bridge = getTauri();
  }

  get isOpen(): boolean {
    return this._open;
  }

  get lastTick(): number {
    return this._lastTick;
  }

  connect(): void {
    if (this._closed) return;
    if (!this.bridge) {
      this.cb.onError?.(
        "no_tauri",
        "StandaloneBattleClient requires the Tauri runtime",
      );
      this.cb.onClose?.({ code: 1006, reason: "no_tauri", wasClean: false });
      return;
    }
    void this._asyncConnect();
  }

  private async _asyncConnect(): Promise<void> {
    const bridge = this.bridge!;
    try {
      this.unlistenFns.push(
        await bridge.listen("battle-frame", (ev) => this._onFrame(ev.payload)),
        await bridge.listen("battle-end", (ev) => this._onEnd(ev.payload)),
        await bridge.listen("battle-error", (ev) =>
          this._onError(ev.payload),
        ),
      );
    } catch (err: any) {
      this.cb.onError?.("listen_failed", String(err?.message || err));
      this.cb.onClose?.({
        code: 1006,
        reason: "listen_failed",
        wasClean: false,
      });
      return;
    }

    try {
      if (this.mode.kind === "host") {
        await bridge.invoke("start_local_battle", {
          args: {
            start_packet: this.mode.startPacket,
            relay_url: this.mode.relayUrl,
            jwt: this.mode.jwt,
            players: this.mode.players,
            host_player_id: this.mode.hostPlayerId,
          },
        });
      } else {
        await bridge.invoke("start_remote_battle", {
          args: {
            relay_url: this.mode.relayUrl,
            jwt: this.mode.jwt,
          },
        });
      }
    } catch (err: any) {
      this.cb.onError?.("start_failed", String(err?.message || err));
      this.cb.onClose?.({
        code: 1006,
        reason: "start_failed",
        wasClean: false,
      });
      this._teardownListeners();
      return;
    }

    this._open = true;
    this.cb.onOpen?.();
    // Worker emits its own match_start equivalent via the first frame; we
    // synthesize a match_start callback here so consumers wire up the same
    // way they do for `WorkerWsClient`.
    this.cb.onMatchStart?.({
      match_id: this.hello.matchId,
      server_tick: 0,
      tick_hz: 0,
    });
    if (this.pending.length) {
      const drain = this.pending;
      this.pending = [];
      for (const order of drain) {
        this.sendOrder(order.action, order.payload);
      }
    }
  }

  sendOrder(action: string, payload: any): boolean {
    if (this._closed) return false;
    if (!this._open || !this.bridge) {
      this.pending.push({ action, payload });
      return true;
    }
    const envelope = { action, payload };
    console.info("[standalone-battle] submit_order", action, payload);
    void this.bridge.invoke("submit_order", { args: { envelope } }).catch(
      (err: any) => {
        console.warn("[standalone-battle] submit_order failed:", err);
      },
    );
    return true;
  }

  close(reason: string = "client_close"): void {
    if (this._closed) return;
    this._closed = true;
    const wasOpen = this._open;
    this._open = false;
    this._teardownListeners();
    if (this.bridge) {
      void this.bridge.invoke("stop_battle").catch(() => {
        /* best-effort */
      });
    }
    if (wasOpen) {
      this.cb.onClose?.({ code: 1000, reason, wasClean: true });
    }
  }

  private _teardownListeners(): void {
    const fns = this.unlistenFns;
    this.unlistenFns = [];
    for (const fn of fns) {
      try {
        fn();
      } catch {
        /* ignore */
      }
    }
  }

  private _onFrame(payload: any): void {
    if (!payload || typeof payload !== "object") return;
    const frame: WorkerTickFrame = {
      tick: Number(payload.tick) || 0,
      phase: String(payload.phase || ""),
      full: !!payload.full,
      units: payload.units,
      enemy_units: payload.enemy_units,
      unit_skills: payload.unit_skills,
      unit_order_queue: payload.unit_order_queue,
      unit_path_waypoints: payload.unit_path_waypoints,
      engagements: payload.engagements,
      projectiles: payload.projectiles,
      messengers: payload.messengers,
      capture_points: payload.capture_points,
      structure_state: payload.structure_state,
      camps: payload.camps,
      events: payload.events,
      visibility: payload.visibility,
      wall_build_projects: payload.wall_build_projects,
      wood_stockpile: payload.wood_stockpile,
      harvest_tiles: payload.harvest_tiles,
      ant_groups: payload.ant_groups,
      terrain_overrides: payload.terrain_overrides,
      watchtowers: payload.watchtowers,
    };
    if (frame.tick > this._lastTick) this._lastTick = frame.tick;
    this.cb.onTickFrame?.(frame);
  }

  private _onEnd(payload: any): void {
    const end: WorkerMatchEnd = {
      match_id: String(payload?.match_id || this.hello.matchId),
      winner: payload?.winner ?? null,
      final_tick: Number(payload?.final_tick) || 0,
      summary: payload?.summary,
      events: payload?.events,
    };
    this.cb.onMatchEnd?.(end);
  }

  private _onError(payload: any): void {
    let code = "battle_error";
    let msg = "";
    if (typeof payload === "string") {
      msg = payload;
      const colon = payload.indexOf(":");
      if (colon > 0) {
        code = payload.slice(0, colon).trim();
        msg = payload.slice(colon + 1).trim();
      }
    } else if (payload && typeof payload === "object") {
      code = String(payload.code || code);
      msg = String(payload.msg || payload.message || "");
    }
    this.cb.onError?.(code, msg);
  }
}
